import { supabase } from '@/utils/supabase';
import { User } from '@supabase/supabase-js';

import logger from '@/utils/logger';
interface UserProfile {
  id: string;
  email: string;
  organization_id: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  role?: string;
}

async function requireLiveSession(expectedUserId?: string): Promise<void> {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session || (expectedUserId && session.user.id !== expectedUserId)) {
    throw new Error('Not authenticated');
  }
}

/**
 * Fetches user profile with retry logic and automatic repair
 */
export async function ensureUserProfile(user: User): Promise<UserProfile> {
  const maxRetries = 3;
  let retryCount = 0;
  let lastError: Error | null = null;

  while (retryCount < maxRetries) {
    try {
      await requireLiveSession(user.id);

      // Attempt to fetch profile
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // If profile exists and has organization_id, we're good
      if (profile?.organization_id) {
        return profile as UserProfile;
      }

      // Profile doesn't exist or missing organization_id - fix it
      logger.info(`Profile issue detected for user ${user.email}, attempting repair...`);

      // Get default organization
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('id')
        .eq('subdomain', 'lendwise')
        .single();

      if (orgError || !org) {
        throw new Error('Default organization not found');
      }

      // Prepare profile data
      const profileData = {
        id: user.id,
        email: user.email || '',
        organization_id: org.id,
        first_name: user.user_metadata?.given_name || 
                   user.user_metadata?.first_name || 
                   (user.email || '').split('@')[0],
        last_name: user.user_metadata?.family_name || 
                  user.user_metadata?.last_name || 
                  '',
        full_name: user.user_metadata?.full_name || 
                   user.user_metadata?.name || 
                   user.email || '',
        role: 'loan_officer',
        is_active: true,
        updated_at: new Date().toISOString()
      };

      // Upsert the profile
      const { data: upsertedProfile, error: upsertError } = await supabase
        .from('users')
        .upsert(profileData)
        .select()
        .single();

      if (upsertError) {
        throw upsertError;
      }

      logger.info(`Profile repaired successfully for user ${user.email}`);
      return upsertedProfile as UserProfile;

    } catch (error) {
      lastError = error as Error;
      if (lastError.message === 'Not authenticated') {
        throw lastError;
      }
      logger.error(`Profile fetch attempt ${retryCount + 1} failed:`, error);
      
      retryCount++;
      if (retryCount < maxRetries) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
    }
  }

  // All retries failed
  throw new Error(`Failed to ensure user profile after ${maxRetries} attempts: ${lastError?.message}`);
}

/**
 * Gets the current user's profile with automatic repair
 */
export async function getCurrentUserProfile(): Promise<UserProfile> {
  await requireLiveSession();

  // Check for development mode impersonation
  if (typeof window !== 'undefined' && localStorage.getItem('dev_impersonated_user')) {
    const impersonatedUserId = localStorage.getItem('dev_impersonated_user');
    const sessionImpersonatedId = sessionStorage.getItem('impersonated_user_id');

    // Fix: Detect orphaned localStorage from incomplete impersonation cleanup
    if (!sessionImpersonatedId && impersonatedUserId) {
      logger.warn('Detected orphaned impersonation in localStorage without sessionStorage - cleaning up', {
        orphanedUserId: impersonatedUserId
      });
      localStorage.removeItem('dev_impersonated_user');
      localStorage.removeItem('dev_user_role');
      // Fall through to normal auth flow below
    } else if (impersonatedUserId) {
      // Valid impersonation state - fetch the impersonated user's profile
      logger.info('Checking for impersonated user:', impersonatedUserId);

      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', impersonatedUserId)
        .single();

      if (error) {
        logger.error('Error fetching impersonated user profile:', error);
      }

      if (profile && !error) {
        logger.info('Using impersonated user profile:', {
          id: profile.id,
          email: profile.email,
          expectedId: impersonatedUserId,
          match: profile.id === impersonatedUserId
        });
        return profile as UserProfile;
      }
    }
  }
  
  // Normal flow - get authenticated user
  // Removed log to reduce console noise
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error || !user) {
    throw new Error('Not authenticated');
  }

  return ensureUserProfile(user);
}

/**
 * Refreshes the current user's profile from the database
 */
export async function refreshUserProfile(): Promise<UserProfile> {
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error || !user) {
    throw new Error('Not authenticated');
  }

  // Force a fresh fetch by clearing any potential cache
  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .single();

  if (profileError || !profile) {
    // If fetch fails, run the repair logic
    return ensureUserProfile(user);
  }

  return profile as UserProfile;
}

/**
 * Debug function to check profile status
 */
export async function debugProfile(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      logger.info('❌ Not authenticated');
      return;
    }

    logger.info('🔍 Debug Profile Information:');
    logger.info('User ID:', user.id);
    logger.info('Email:', user.email);
    logger.info('Metadata:', user.user_metadata);

    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      logger.info('❌ Profile fetch error:', error);
    } else if (!profile) {
      logger.info('❌ No profile found');
    } else {
      logger.info('✅ Profile found:', profile);
      
      if (!profile.organization_id) {
        logger.info('⚠️ Profile missing organization_id');
      }
    }

    // Check organization
    if (profile?.organization_id) {
      const { data: org } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.organization_id)
        .single();
      
      logger.info('🏢 Organization:', org);
    }

  } catch (error) {
    logger.error('Debug error:', error);
  }
}

// Make debug function available globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as Window & { debugProfile?: typeof debugProfile }).debugProfile = debugProfile;
}
